import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

const dashboardFeatures = [
  "Real-time approval tracking across all departments",
  "Centralized document, notice, and request management",
  "Unified workflow across teams and operations",
  "Asset inventory with real-time utilization insights",
  "Fleet and vehicle usage tracking at a glance",
];

// Each card: main (tall/portrait) image + optional pip (wide/landscape overlay)
const imageCards = [
  {
    // 1-1 is portrait (tall) → main, 1 is landscape (wide) → pip
    main: "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/464a169a1c51d65db7e891acb7b019ae.png",
    pip: "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/d4806fa0e70da229fe6a7e872b1a6429.png",
    tilt: "-6deg",
    delay: 0,
  },
  {
    // 2-1 is portrait (tall) → main, 2 is landscape (wide) → pip
    main: "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/4978625cc197461308df63c2489daf33.png",
    pip: "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/0c445e93bceb038c0e538e482efee2d0.png",
    tilt: "-2deg",
    delay: 100,
  },
  {
    // 3-1 is portrait (tall) → main, 3 is landscape (wide) → pip
    main: "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/c869f5b143110bfd6c71ce3ce3db42f1.png",
    pip: "https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/5ae91e065566f74f2fe36191ce3d543a.png",
    tilt: "3deg",
    delay: 200,
  },
];

export default function DashboardPreviewSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="dashboard" className="py-24 bg-slate-50 overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* ── 1. Top text: badge + title + description — centered ── */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-4 block">
            Real-Time Operations
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-5">
            Manage Your Entire Workplace in One Screen
          </h2>
          <p className="text-slate-500 text-base leading-relaxed max-w-2xl mx-auto">
            Track approvals, requests, and updates in real-time — all from a single workspace.
          </p>
        </div>

        {/* ── 2. Three tilted composite cards ── */}
        <div
          id="card-row"
          className={`w-full mb-3 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="flex items-end justify-center px-2" style={{ gap: 0 }}>
            {imageCards.map((card, i) => (
              <div
                key={i}
                className="relative cursor-pointer group"
                style={{
                  transform: `rotate(${card.tilt})`,
                  transformOrigin: "bottom center",
                  transition: `transform 0.35s ease, opacity 0.6s ease ${card.delay}ms`,
                  width: 300,
                  flexShrink: 0,
                  marginLeft: i === 0 ? 0 : -40,
                  zIndex: i === 1 ? 10 : 5,
                  opacity: isVisible ? 1 : 0,
                }}
              >
                <div
                  className="rounded-lg overflow-hidden border border-slate-200 group-hover:-translate-y-2 transition-transform duration-300"
                  style={{ boxShadow: "0 12px 40px rgba(15,23,42,0.18)" }}
                >
                  <img
                    src={card.main}
                    alt={`Feature screenshot ${i + 1}`}
                    className="w-full h-auto"
                    style={{ display: "block" }}
                  />
                </div>
                {card.pip && (
                  <div
                    className="absolute bottom-4 right-4 rounded-md overflow-hidden border-2 border-white"
                    style={{ width: "58%", boxShadow: "0 4px 18px rgba(15,23,42,0.28)", zIndex: 10 }}
                  >
                    <img
                      src={card.pip}
                      alt={`Feature detail ${i + 1}`}
                      className="w-full h-auto"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── 3. Full-width main dashboard image (contained) ── */}
        <div
          className={`flex justify-center w-full mb-14 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div
            className="group cursor-pointer relative"
            style={{ maxWidth: 660, width: "100%" }}
          >
            <div
              className="rounded-2xl overflow-hidden border border-slate-200 group-hover:-translate-y-2 transition-transform duration-300"
              style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.22), 0 8px 24px rgba(15,23,42,0.14)" }}
            >
              <img
                src="https://static.readdy.ai/image/22e74f403809c5eb5ed859e1f3746460/dba74d0e80ff235f7cfaeff5472760b5.png"
                alt="BizLab Main Dashboard"
                className="w-full h-auto object-top"
              />
            </div>
          </div>
        </div>

        {/* ── 4. Feature cards — icon top + checkmark + text below ── */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-10 transition-all duration-700 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {dashboardFeatures.map((feat, idx) => (
            <div
              key={feat}
              className="flex flex-col items-center text-center bg-white border border-slate-100 rounded-2xl px-5 py-6 gap-4"
              style={{ boxShadow: "0 2px 16px rgba(15,23,42,0.07)" }}
            >
              {/* Top icon in rounded square */}
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-blue-50 shrink-0">
                <i className={[
                  "ri-bar-chart-2-line",
                  "ri-folder-3-line",
                  "ri-team-line",
                  "ri-box-3-line",
                  "ri-car-line",
                ][idx] + " text-blue-500 text-2xl"} />
              </div>
              {/* Checkmark + text */}
              <div className="flex items-start gap-2 text-left">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-blue-500 text-lg" />
                </div>
                <span className="text-slate-700 text-sm font-medium leading-snug">{feat}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── 5. CTA button — centered ── */}
        <div
          className={`flex justify-center transition-all duration-700 delay-400 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <button
            onClick={() => {
              const el = document.querySelector("#contact");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold rounded-full transition-all duration-200 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            View Product Tour
            <i className="ri-arrow-right-line" />
          </button>
        </div>

      </div>
    </section>
  );
}
